import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';

import { selectCurrentUser } from 'store/user/selectors';
import { selectDiscountSubscription } from 'store/subscriptions/selectors';

import { SUBSCRIPTION_STATES } from 'constants/subscriptions';

import useModals from 'modals/hooks';
import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import { checkDiscountAllowed } from '../../../hooks/helpers';

import { isMainSubscription } from 'helpers/subscriptions';

import { ModalName } from 'modals/types';

import { CancellationFlowStrategy } from '../enums';

type StrategyMap = Partial<Record<CancellationFlowStrategy, () => void>>;

const useCancellationStrategy = () => {
    const { cancellationCandidates, onCancelSubscription } = useCancelSubscriptionFlow();

    const { product_type, discount, state, reminder } = cancellationCandidates?.[0] ?? {};

    const { country } = useSelector(selectCurrentUser);
    const discountSubscription = useSelector(selectDiscountSubscription);

    const { openModal } = useModals();

    const runDiscountFlow = useCallback(() => {
        const isDiscountAllowed = !discount?.discount_type && checkDiscountAllowed(country);

        const isMain = product_type ? isMainSubscription(product_type) : false;

        isMain && isDiscountAllowed && discountSubscription
            ? openModal(ModalName.DiscountModal)
            : onCancelSubscription();
    }, [discount?.discount_type, country, product_type, discountSubscription, openModal, onCancelSubscription]);

    const runFreeMonthPauseFlow = useCallback(
        (modalId: ModalName) => {
            const isFreeMonthAllowed = state !== SUBSCRIPTION_STATES.Paused;

            isFreeMonthAllowed ? openModal(modalId) : onCancelSubscription();
        },
        [openModal, onCancelSubscription, state]
    );

    const runReminderFlow = useCallback(
        () => (reminder ? onCancelSubscription() : openModal(ModalName.ReminderModal)),
        [onCancelSubscription, reminder, openModal]
    );

    const strategies: StrategyMap = useMemo(
        () => ({
            [CancellationFlowStrategy.Discount]: runDiscountFlow,
            [CancellationFlowStrategy.HelpLogin]: () => openModal(ModalName.CanNotLoginModal),
            [CancellationFlowStrategy.Reminder]: runReminderFlow,
            [CancellationFlowStrategy.FreeMonth]: () => runFreeMonthPauseFlow(ModalName.FreeMonthModal),
            [CancellationFlowStrategy.Pause]: () => runFreeMonthPauseFlow(ModalName.PauseModal),
            [CancellationFlowStrategy.ChangePlan]: () => openModal(ModalName.AdditionalContentOfferModal),
        }),
        [runDiscountFlow, openModal, runFreeMonthPauseFlow, runReminderFlow]
    );

    const determineCancelStrategy = useCallback(
        (flow: CancellationFlowStrategy | undefined) => {
            const strategy = flow && strategies[flow];

            strategy ? strategy() : onCancelSubscription();
        },
        [onCancelSubscription, strategies]
    );

    return { determineCancelStrategy };
};

export default useCancellationStrategy;
